import * as React from 'react';
import { Check, ChevronsUpDown, X, MapPin, Users, Box, ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Badge } from './badge';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
	CommandSeparator,
} from './command';

/**
 * AssetGroupSelect - A searchable multi-select component with separate sections for groups and individual assets
 *
 * @param {Object} props
 * @param {Array} props.assets - Array of asset objects with relationships
 * @param {Array} props.assetGroups - Array of asset group objects (each with id, name, description, assets array)
 * @param {Array} props.selectedAssetIds - Array of selected individual asset IDs
 * @param {string|null} props.selectedGroupId - Selected group ID (null if no group selected)
 * @param {Function} props.onAssetIdsChange - Callback when asset selection changes
 * @param {Function} props.onGroupIdChange - Callback when group selection changes
 * @param {string} props.placeholder - Placeholder text
 * @param {boolean} props.disabled - Disable the component
 */
export function AssetGroupSelect({
	assets = [],
	assetGroups = [],
	selectedAssetIds = [],
	selectedGroupId = null,
	onAssetIdsChange,
	onGroupIdChange,
	placeholder = 'Select group or assets...',
	disabled = false,
	className,
}) {
	const [open, setOpen] = React.useState(false);
	const [search, setSearch] = React.useState('');
	const [expandedGroups, setExpandedGroups] = React.useState({});

	// Toggle group expansion
	const toggleGroupExpansion = (groupId, e) => {
		e.stopPropagation();
		setExpandedGroups((prev) => ({
			...prev,
			[groupId]: !prev[groupId],
		}));
	};

	// Get asset details for display
	const getAssetDisplayInfo = (asset) => {
		const manufacturer = asset.manufacturer?.name || asset.manufacturerName || '';
		const model = asset.model?.name || asset.modelName || '';
		const serial = asset.serialNumber || '';
		const location = asset.location?.name || '';

		return {
			manufacturer,
			model,
			serial,
			location,
			displayName: `${manufacturer} ${model}`.trim() || 'Unknown Asset',
			fullDisplay: `${manufacturer} ${model} (S/N: ${serial})${location ? ` - ${location}` : ''}`,
		};
	};

	// Get selected group object
	// Use string comparison to handle potential type mismatches (UUID strings)
	const selectedGroup = React.useMemo(() => {
		if (!selectedGroupId) return null;
		return assetGroups.find((g) => String(g.id) === String(selectedGroupId));
	}, [assetGroups, selectedGroupId]);

	// Check if a group is effectively selected (all its assets are selected)
	const isGroupEffectivelySelected = React.useCallback((group) => {
		if (!group?.assets?.length) return false;
		const groupAssetIds = group.assets.map((a) => String(a.id));
		return groupAssetIds.every((id) => selectedAssetIds.map(String).includes(id));
	}, [selectedAssetIds]);

	// Get selected assets for display
	const selectedAssets = React.useMemo(() => {
		return (assets || []).filter((asset) => selectedAssetIds.includes(asset.id));
	}, [assets, selectedAssetIds]);

	// Filter and sort groups based on search
	const filteredGroups = React.useMemo(() => {
		let groups = assetGroups;
		if (search) {
			const term = search.toLowerCase();
			groups = assetGroups.filter(
				(group) =>
					group.name?.toLowerCase().includes(term) ||
					group.description?.toLowerCase().includes(term)
			);
		}
		// Sort groups alphabetically by name
		return [...groups].sort((a, b) =>
			(a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase())
		);
	}, [assetGroups, search]);

	// Filter assets based on search
	const filterAssets = (asset, searchTerm) => {
		if (!searchTerm) return true;
		const term = searchTerm.toLowerCase();
		const manufacturer = (asset.manufacturer?.name || asset.manufacturerName || '').toLowerCase();
		const model = (asset.model?.name || asset.modelName || '').toLowerCase();
		const serial = (asset.serialNumber || '').toLowerCase();
		const location = (asset.location?.name || '').toLowerCase();
		const tag = (asset.tag || '').toLowerCase();

		return (
			manufacturer.includes(term) ||
			model.includes(term) ||
			serial.includes(term) ||
			location.includes(term) ||
			tag.includes(term)
		);
	};

	const filteredAssets = React.useMemo(() => {
		return (assets || []).filter((asset) => filterAssets(asset, search));
	}, [assets, search]);

	// Group filtered assets by manufacturer for display, sorted by manufacturer name then model name
	const groupedAssets = React.useMemo(() => {
		const groups = {};
		filteredAssets.forEach((asset) => {
			const manufacturer = asset.manufacturer?.name || asset.manufacturerName || 'Other';
			if (!groups[manufacturer]) {
				groups[manufacturer] = [];
			}
			groups[manufacturer].push(asset);
		});

		// Sort assets within each group by model name
		Object.keys(groups).forEach((manufacturer) => {
			groups[manufacturer].sort((a, b) => {
				const modelA = (a.model?.name || a.modelName || '').toLowerCase();
				const modelB = (b.model?.name || b.modelName || '').toLowerCase();
				return modelA.localeCompare(modelB);
			});
		});

		// Return sorted by manufacturer name (Object.entries will be sorted when rendered)
		const sortedGroups = {};
		Object.keys(groups)
			.sort((a, b) => {
				// "Other" always goes last
				if (a === 'Other') return 1;
				if (b === 'Other') return -1;
				return a.toLowerCase().localeCompare(b.toLowerCase());
			})
			.forEach((key) => {
				sortedGroups[key] = groups[key];
			});

		return sortedGroups;
	}, [filteredAssets]);

	// Handle selecting a group
	const handleSelectGroup = (groupId) => {
		// Use string comparison for consistency
		if (selectedGroupId && String(selectedGroupId) === String(groupId)) {
			// Deselect group
			onGroupIdChange(null);
			onAssetIdsChange([]);
		} else {
			// Select group and its assets
			const group = assetGroups.find((g) => String(g.id) === String(groupId));
			const groupAssetIds = group?.assets?.map((a) => a.id) || [];
			onGroupIdChange(groupId);
			onAssetIdsChange(groupAssetIds);
		}
	};

	// Handle selecting an individual asset
	const handleSelectAsset = (assetId) => {
		// Clear group selection when selecting individual assets
		if (selectedGroupId) {
			onGroupIdChange(null);
		}

		const newValue = selectedAssetIds.includes(assetId)
			? selectedAssetIds.filter((id) => id !== assetId)
			: [...selectedAssetIds, assetId];
		onAssetIdsChange(newValue);
	};

	// Handle removing an asset
	const handleRemoveAsset = (assetId, e) => {
		e.stopPropagation();
		// If part of a group, clear the group selection
		if (selectedGroupId) {
			onGroupIdChange(null);
		}
		onAssetIdsChange(selectedAssetIds.filter((id) => id !== assetId));
	};

	// Handle clearing group
	const handleClearGroup = (e) => {
		e.stopPropagation();
		onGroupIdChange(null);
		onAssetIdsChange([]);
	};

	// Handle clearing all
	const handleClearAll = (e) => {
		e.stopPropagation();
		onGroupIdChange(null);
		onAssetIdsChange([]);
	};

	const hasSelection = selectedGroup || selectedAssets.length > 0;

	return (
		<div className={cn('w-full', className)}>
			<Popover open={open} onOpenChange={setOpen}>
				<PopoverTrigger asChild disabled={disabled}>
					<button
						type="button"
						role="combobox"
						aria-expanded={open}
						className={cn(
							'flex min-h-[42px] w-full items-center justify-between rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm ring-offset-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
							hasSelection && 'h-auto'
						)}
						disabled={disabled}
					>
						<div className="flex flex-1 flex-wrap gap-1.5">
							{!hasSelection ? (
								<span className="text-gray-400">{placeholder}</span>
							) : selectedGroup ? (
								<Badge
									variant="secondary"
									className="flex items-center gap-1 bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100"
								>
									<Users className="h-3 w-3" />
									<span className="max-w-[200px] truncate">
										{selectedGroup.name} ({selectedGroup.assets?.length || 0} assets)
									</span>
									<button
										type="button"
										className="ml-1 rounded-full outline-none ring-offset-white focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
										onClick={handleClearGroup}
									>
										<X className="h-3 w-3 text-purple-600 hover:text-purple-800" />
									</button>
								</Badge>
							) : (
								selectedAssets.map((asset) => {
									const info = getAssetDisplayInfo(asset);
									return (
										<Badge
											key={asset.id}
											variant="secondary"
											className="flex items-center gap-1 bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100"
										>
											<span className="max-w-[200px] truncate">
												{info.displayName} ({info.serial})
											</span>
											<button
												type="button"
												className="ml-1 rounded-full outline-none ring-offset-white focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
												onClick={(e) => handleRemoveAsset(asset.id, e)}
											>
												<X className="h-3 w-3 text-indigo-600 hover:text-indigo-800" />
											</button>
										</Badge>
									);
								})
							)}
						</div>
						<div className="flex items-center gap-1 ml-2">
							{hasSelection && (
								<button
									type="button"
									className="rounded-full p-0.5 hover:bg-gray-100"
									onClick={handleClearAll}
								>
									<X className="h-4 w-4 text-gray-400 hover:text-gray-600" />
								</button>
							)}
							<ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
						</div>
					</button>
				</PopoverTrigger>
				<PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
					<Command shouldFilter={false}>
						<CommandInput
							placeholder="Search groups or assets..."
							value={search}
							onValueChange={setSearch}
						/>
						<CommandList>
							<CommandEmpty>No groups or assets found.</CommandEmpty>

							{/* Asset Groups Section */}
							{filteredGroups.length > 0 && (
								<CommandGroup heading="Asset Groups">
									{filteredGroups.map((group) => {
										// Check if selected by groupId OR if all group assets are selected
										// Use string comparison to handle potential type mismatches
										const isSelected = (selectedGroupId && String(selectedGroupId) === String(group.id)) || isGroupEffectivelySelected(group);
										const assetCount = group.assets?.length || 0;
										const isExpanded = expandedGroups[group.id];

										return (
											<React.Fragment key={group.id}>
												<CommandItem
													value={`group-${group.id}`}
													onSelect={() => handleSelectGroup(group.id)}
													className="flex items-center gap-2"
												>
													<div
														className={cn(
															'flex h-4 w-4 items-center justify-center rounded border',
															isSelected
																? 'border-purple-600 bg-purple-600 text-white'
																: 'border-gray-300'
														)}
													>
														{isSelected && <Check className="h-3 w-3" />}
													</div>
													<button
														type="button"
														onClick={(e) => toggleGroupExpansion(group.id, e)}
														className="p-0.5 hover:bg-gray-100 rounded"
													>
														{isExpanded ? (
															<ChevronDown className="h-4 w-4 text-gray-500" />
														) : (
															<ChevronRight className="h-4 w-4 text-gray-500" />
														)}
													</button>
													<Users className="h-4 w-4 text-purple-500" />
													<div className="flex flex-1 flex-col">
														<div className="flex items-center gap-2">
															<span className="font-medium">{group.name}</span>
															<span className="text-xs text-gray-500">
																({assetCount} asset{assetCount !== 1 ? 's' : ''})
															</span>
														</div>
														{group.description && (
															<span className="text-xs text-gray-500">
																{group.description}
															</span>
														)}
													</div>
												</CommandItem>
												{/* Show assets within the group when expanded */}
												{isExpanded && group.assets?.length > 0 && (
													<div className="ml-8 border-l-2 border-purple-100 pl-2">
														{group.assets.map((asset) => {
															const info = getAssetDisplayInfo(asset);
															return (
																<div
																	key={asset.id}
																	className="flex items-center gap-2 py-1.5 px-2 text-sm text-gray-600"
																>
																	<Box className="h-3 w-3 text-gray-400" />
																	<span>{info.displayName}</span>
																	{info.serial && (
																		<span className="text-xs text-gray-400">
																			(S/N: {info.serial})
																		</span>
																	)}
																</div>
															);
														})}
													</div>
												)}
											</React.Fragment>
										);
									})}
								</CommandGroup>
							)}

							{/* Separator between groups and assets */}
							{filteredGroups.length > 0 && Object.keys(groupedAssets).length > 0 && (
								<CommandSeparator />
							)}

							{/* Individual Assets Section - grouped by manufacturer */}
							{Object.entries(groupedAssets).map(([manufacturer, manufacturerAssets]) => (
								<CommandGroup key={manufacturer} heading={manufacturer}>
									{manufacturerAssets.map((asset) => {
										const info = getAssetDisplayInfo(asset);
										const isSelected = selectedAssetIds.includes(asset.id);

										return (
											<CommandItem
												key={asset.id}
												value={asset.id}
												onSelect={() => handleSelectAsset(asset.id)}
												className="flex items-center gap-2"
											>
												<div
													className={cn(
														'flex h-4 w-4 items-center justify-center rounded border',
														isSelected
															? 'border-indigo-600 bg-indigo-600 text-white'
															: 'border-gray-300'
													)}
												>
													{isSelected && <Check className="h-3 w-3" />}
												</div>
												<Box className="h-4 w-4 text-gray-400" />
												<div className="flex flex-1 flex-col">
													<div className="flex items-center gap-2">
														<span className="font-medium">{info.model || 'Unknown Model'}</span>
														<span className="text-xs text-gray-500">
															S/N: {info.serial || 'N/A'}
														</span>
													</div>
													{info.location && (
														<div className="flex items-center gap-1 text-xs text-gray-500">
															<MapPin className="h-3 w-3" />
															<span>{info.location}</span>
														</div>
													)}
												</div>
											</CommandItem>
										);
									})}
								</CommandGroup>
							))}
						</CommandList>
					</Command>
				</PopoverContent>
			</Popover>

			{/* Count indicator */}
			{hasSelection && (
				<p className="mt-1.5 text-xs text-gray-500">
					{selectedGroup
						? `Group "${selectedGroup.name}" selected (${selectedGroup.assets?.length || 0} assets)`
						: `${selectedAssets.length} asset${selectedAssets.length !== 1 ? 's' : ''} selected`}
				</p>
			)}
		</div>
	);
}

export default AssetGroupSelect;
